import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, Ticket, Calendar, MapPin, Clock, Loader2, Download, Gift, User } from 'lucide-react';
import { KickbackShareCard } from '../components/KickbackShareCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/tickets/confirm/${orderId}`);
      // Door sale: jump straight to the fullscreen QR wallet so the
      // ticket is on screen and scannable immediately.
      if (searchParams.get('door') === '1' && res.data?.wallet_url) {
        window.location.replace(res.data.wallet_url);
        return;
      }
      setData(res.data);
    } catch { toast.error('Failed to load order'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center pt-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center pt-20"><p>Order not found</p></div>;

  const { order, tickets, event } = data;
  const addonItems = (order?.items || []).filter(i => i.is_addon);

  const fmtDate = (d) => {
    try {
      const dt = new Date(`${d}T12:00:00`);
      if (isNaN(dt)) return d;
      return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };
  const fmtTime = (t) => {
    const [h, m] = String(t).split(':').map(Number);
    if (isNaN(h)) return t;
    return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-lg mx-auto px-4">
        <div className="text-center mb-8">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-foreground">Tickets Confirmed!</h1>
          <p className="text-sm text-muted-foreground mt-1">A confirmation email has been sent to {order?.buyer_email}</p>
        </div>

        {/* 1. Event Info — confirmation details come first */}
        {event && (
          <Card className="bg-card border-border mb-4">
            <CardContent className="p-4">
              <h2 className="font-semibold text-lg text-foreground">{event.title || event.name}</h2>
              {event.organizer?.name && (
                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid="confirmation-organizer">
                  <User className="h-3 w-3" /> Organizer:{' '}
                  {event.organizer.slug ? (
                    <Link to={`/organizations/${event.organizer.slug}`} className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2" data-testid="confirmation-organizer-link">
                      {event.organizer.name}
                    </Link>
                  ) : (
                    <span className="text-foreground">{event.organizer.name}</span>
                  )}
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                {event.event_date && <span className="flex items-center gap-1" data-testid="confirmation-event-date"><Calendar className="h-3 w-3" /> {fmtDate(event.event_date)}</span>}
                {event.event_time && <span className="flex items-center gap-1" data-testid="confirmation-event-time"><Clock className="h-3 w-3" /> {fmtTime(event.event_time)}</span>}
                {event.venue_name && (
                  event.venue_slug ? (
                    <Link to={`/${(event.city || '').toLowerCase()}/venues/${event.venue_slug}`} className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 underline underline-offset-2" data-testid="confirmation-venue-link">
                      <MapPin className="h-3 w-3" /> {event.venue_name}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.venue_name}</span>
                  )
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 2. Open Digital Wallet CTA — holds the swipe-able QR tickets */}
        {data?.wallet_url && (
          <a href={data.wallet_url} className="block mb-4" data-testid="confirmation-open-wallet">
            <div className="rounded-2xl px-6 py-4 text-white text-center font-semibold shadow-lg hover:opacity-95 transition" style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed, #ec4899)' }}>
              ✨ Open your Digital Wallet →
              <p className="text-[11px] font-normal opacity-90 mt-0.5">Swipe-able tickets with QR codes — perfect for the door</p>
            </div>
          </a>
        )}

        {/* 3. Kickback share prompt */}
        <KickbackShareCard orderId={orderId} />

        {/* Ticket QR fallback — only when there is no digital wallet */}
        {!data?.wallet_url && (
        <div className="space-y-3" data-testid="tickets-list">
          {tickets?.map(ticket => (
            <Card key={ticket.ticket_id} className="bg-card border-border overflow-hidden" data-testid={`ticket-${ticket.ticket_code}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-foreground">{ticket.ticket_type_name}</p>
                    <Badge variant="outline" className="text-[10px] capitalize">{ticket.tier}</Badge>
                  </div>
                  <p className="font-mono text-sm font-bold text-indigo-400">{ticket.ticket_code}</p>
                </div>
                {/* QR Code */}
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={`data:image/png;base64,${ticket.qr_code_base64}`} alt="QR Code" className="w-40 h-40" />
                </div>
                <p className="text-center text-xs text-muted-foreground mt-2">Present this QR code at the door for check-in</p>
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {/* Add-ons */}
        {addonItems.length > 0 && (
          <Card className="bg-card border-fuchsia-500/30 mt-4" data-testid="confirmation-addons">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="h-4 w-4 text-fuchsia-400" />
                <p className="font-semibold text-sm">Your Add-ons</p>
              </div>
              <div className="space-y-2">
                {addonItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm" data-testid={`confirmation-addon-${item.ticket_type_id}`}>
                    <span>{item.name}{item.variation_name ? ` — ${item.variation_name}` : ''}</span>
                    <Badge className="bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30">x {item.quantity}</Badge>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">Show this confirmation (or your email) at the event to redeem your add-ons.</p>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-3 mt-6">
          <Button className="flex-1" variant="outline" onClick={() => navigate('/my-tickets')}>
            <Ticket className="h-4 w-4 mr-2" /> My Tickets
          </Button>
          <Button className="flex-1" onClick={() => navigate('/events')}>
            Browse Events
          </Button>
        </div>
      </div>
    </div>
  );
}
